import React from 'react';
import { useTranslation } from 'react-i18next';
import SearchBar from '../components/Searchbar';
import Card from '../components/Card';
import { FaMoneyBillTrendUp, FaRegFileLines, FaRegIdCard, FaRegCreditCard, FaMoneyBillTransfer } from "react-icons/fa6";
import { RiFormula } from "react-icons/ri";
import { TbApi } from "react-icons/tb";
import { GrContact } from "react-icons/gr";
import EmblemLight from '../assets/Logos/EmblemLight.svg';
import EmblemDark from '../assets/Logos/EmblemDark.svg';
import Meta from "../components/dynamicMeta"
import { dataCy, SELECTORS } from '../utils/dataCy';
import { useLogin } from '../components/logIncontext';

const Home = () => {
  const { t, i18n } = useTranslation('home');
  const { userData } = useLogin();
  // Forzar re-render cuando cambia userData
  const categories = t('categories', { returnObjects: true }) || [];

  const currentLang = i18n.language;



  // Componente dinámico para los emblemas
  const Emblems = () => (
    <div className="relative w-5 h-5 mr-1">
      <img
        src={EmblemLight}
        alt="Emblem Icon Light"
        className="w-full h-full dark:hidden"
      />
      <img
        src={EmblemDark}
        alt="Emblem Icon Dark"
        className="w-full h-full hidden dark:block"
      />
    </div>
  );

  // Mapeo de íconos
  const iconMapping = {
    Emblem: Emblems,
    FaMoneyBillTrendUp: FaMoneyBillTrendUp,
    FaRegFileLines: FaRegFileLines,
    FaRegIdCard: FaRegIdCard,
    FaRegCreditCard: FaRegCreditCard,
    FaMoneyBillTransfer: FaMoneyBillTransfer,
    TbApi: TbApi,
    RiFormula: RiFormula,
  };


  return (
    <div>
      <Meta />
      <div className="pt-[7rem] sm:pt[7rem] md:pt-[9rem] lg:pt-[9rem] xl:pt-[9rem] 2xl:pt-[9rem] px-4 sm:px-28 py-8 sm:py-16 md:px-16">
        {/* Hero Section */}
        <section
          className="text-center bg-gradient-to-br from-primaryBg to-secondGradient text-white dark:from-blueMenu dark:to-popup py-16 sm:py-20 rounded-lg shadow-lg"
        >
          <h1 className="text-4xl px-4 sm:text-5xl font-extrabold mb-4">{t('welcomeTitle')}</h1>
          <h2 className="text-3xl px-4 sm:text-4xl font-bold mb-4">{t('welcomeSubitle')}</h2>
          <p className="text-base px-4 sm:text-lg mb-6">{t('welcomeMessage')}</p>
          <div className="max-w-2xl mx-auto px-4 color-black">
            <SearchBar categories={categories} currentLang={currentLang} t={t} />
          </div>
        </section>

        {/* Sección de categorías */}
        <section className="py-8 lg:py-16 mb:py-16">
          <h2 className="text-2xl sm:text-3xl font-semibold mb-6 text-center text-gray-800 dark:text-white">
            {t('homeContent.exploreCategories')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {categories.filter(category => {
              if (category.slug === 'api') {
                const isLocalhost = window.location.hostname === 'localhost';
                const apiEnabled = userData?.config?.apiEnabled;
                return isLocalhost || apiEnabled;
              }
              const formulasSlugs = ['formulas', 'formules', 'formule', 'formulas-zh'];
              if (formulasSlugs.includes(category.slug)) {
                const isLocalhost = window.location.hostname === 'localhost';
                const formulasEnabled = userData?.config?.formulasEnabled;
                console.log('🔍 Formulas filter - hostname:', window.location.hostname, 'isLocalhost:', isLocalhost, 'formulasEnabled:', formulasEnabled);
                return isLocalhost || formulasEnabled;
              }
              return true;
            }).map((category, index) => {
              // Función recursiva para contar artículos en subcategorías anidadas
              const countArticlesRecursively = (items) => {
                if (!items) return 0;
                return items.reduce((sum, item) => {
                  let count = item.articles ? item.articles.length : 0;
                  if (item.subcategories) {
                    count += countArticlesRecursively(item.subcategories);
                  }
                  return sum + count;
                }, 0);
              };

              // Calcular el total de artículos (incluyendo subcategorías anidadas)
              let totalArticles = category.articles ? category.articles.length : 0;
              if (category.subcategories) {
                totalArticles += countArticlesRecursively(category.subcategories);
              }

              // Función recursiva para encontrar el primer artículo
              const findFirstArticle = (items, basePath = '') => {
                if (!items) return null;
                
                for (const item of items) {
                  if (item.articles && item.articles.length > 0) {
                    return {
                      article: item.articles[0],
                      path: `${basePath}/${item.slug}/${item.articles[0].slug}`
                    };
                  }
                  if (item.subcategories) {
                    const result = findFirstArticle(item.subcategories, `${basePath}/${item.slug}`);
                    if (result) return result;
                  }
                }
                return null;
              };

              // Determinar el primer artículo para el enlace
              let firstArticle = null;
              let linkPath = '';

              if (category.articles && category.articles.length > 0) {
                firstArticle = category.articles[0];
                linkPath = `/${i18n.language}/${category.slug}/${firstArticle.slug}`;
              } else if (category.subcategories && category.subcategories.length > 0) {
                const result = findFirstArticle(category.subcategories, `/${i18n.language}/${category.slug}`);
                if (result) {
                  firstArticle = result.article;
                  linkPath = result.path;
                }
              }

              if (!firstArticle) return null;

              const Icon = iconMapping[category.icon];

              return (
                <Card
                  key={index}
                  title={category.title}
                  description={`${totalArticles} ${t('articles')}`}
                  link={linkPath}
                  Icon={Icon}
                />
              );
            })}

          </div>
        </section>

        {/* Sección de no encontraste lo que necesitabas */}
        <section className="py-12 sm:py-16 bg-gray-100 dark:bg-grayButton rounded-lg">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl px-4 sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {t('homeContent.didntFindAnswer')}
            </h2>
            <p className="text-base px-4 sm:text-lg text-gray-700 dark:text-gray-300 mb-8">
              {t('homeContent.teamReadyToHelp')}
            </p>
            <div className="mt-6 flex justify-center">
              <a
                href={`/${i18n.language}/contact`}
                className="flex flex-row gap-2 md:w-auto bg-[#086ef9] border-2 border-[#3288ff] text-white text-[0.9rem] px-[60px] sm:px-[60px] md:px-[60px] lg:px-[60px] xl:px-[60px] py-[12px] rounded-[8px] no-underline inline-block mr-2 hover:border-[#086ef9] transition-colors cursor-pointer"
                data-cy={dataCy(SELECTORS.LINK, 'home-contact')}
              >
                <GrContact className="text-xl" />
                {t('getInTouch', 'Contact Us')}
              </a>
            </div>
          </div>
        </section>
      </div>
    </div>

  );
};

export default Home;
